import type { AdminUser, AuditEntry, User } from '../../api'

/*
 * The console's user list and card (BILL-B6a): types of GET /api/admin/users?limit=…,
 * /api/admin/users/{id}/overview and …/broadcasts (docs/controlapi.yaml), the filters as URL
 * parameters (a link to a filtered list can be sent), the presets of FilterPresets and the
 * words of hours (UsageMeters: no decimals; whole hours without minutes; under 1 h minutes only).
 */

export type UsageKey = 'stream_hours_month' | 'captions_hours_month'

export interface AdminQuota {
  /** seconds */
  used: number
  /** seconds; null = no limit, 0 = not on the plan */
  limit: number | null
  percent: number | null
  level: 0 | 80 | 100
  /** when the usage watchdog counted it; null = not on air this period */
  computed_at: string | null
}

export interface AdminUserBilling {
  plan_id: string
  plan_name: string
  plan_version: number
  source: 'subscription' | 'free' | 'grant' | 'legacy' | 'ambassador'
  subscription_status: '' | 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled' | 'trial_expired'
  access: 'full' | 'no_new_streams'
  access_reason?: string
  vip: boolean
  trial_ends_at?: string
  plan_until?: string
  period_end?: string
  ends_at?: string
  ends_kind?: 'grant' | 'legacy' | 'ambassador' | 'trial' | 'period'
  usage_period: { start: string; end: string; kind: 'subscription' | 'trial' | 'calendar' }
  usage_computed_at: string | null
  usage: Partial<Record<UsageKey, AdminQuota>>
  destinations_limit: number | null
  has_holds: boolean
}

export interface AdminUserRow extends AdminUser {
  broadcasts_30d: number
  /** absent without billing.read or with billing off; not `billing`: on User that is the /api/me BillingView */
  billing_summary?: AdminUserBilling
}

export interface AdminUsersPage {
  users: AdminUserRow[]
  total: number
  limit: number
  offset: number
  presets: Partial<Record<string, number>>
}

export interface AdminBroadcast {
  id: number
  stream_name: string
  started_at: string
  ended_at: string | null
  live: boolean
  seconds: number
  node: string
  outputs: number
  captions_seconds: number
}

export interface AdminBroadcastPage {
  items: AdminBroadcast[]
  next_cursor: string
}

export interface AdminUsageDay {
  day: string
  stream_hours_month: number
  captions_hours_month: number
}

export interface AdminUserOverview extends AdminUserRow {
  last_sign_in_at: string | null
  billing_card?: string
  usage_daily?: { days: 30 | 90; from: string; to: string; items: AdminUsageDay[] }
  broadcasts: AdminBroadcastPage
  destinations_by_platform: { count: number; by_platform: Record<string, number> }
  devices: number
  api_tokens: number
  activity?: AuditEntry[]
}

/* ── rights (RBAC-1: buttons stay visible, disabled with the permission they need) ── */

export type Permission = 'users.read' | 'users.manage' | 'billing.read' | 'billing.manage'

/** The console part of /api/me; absent = not a member (or an older center: every right). */
export function can(u: User | null | undefined, p: Permission): boolean {
  const c = (u as (User & { console?: { permissions?: string[]; enforced?: boolean } }) | null | undefined)?.console
  if (!c || !c.enforced) return true
  return !!c.permissions?.includes(p)
}

/* ── filters in the URL ── */

/** Access in the dropdown: Active (full, not blocked), Limited (no new broadcasts), Blocked. */
export type AccessFilter = '' | 'active' | 'limited' | 'blocked'
export type SourceFilter = '' | 'subscription' | 'free' | 'grant' | 'legacy' | 'ambassador'
export type SortKey = 'email' | 'created' | 'last_broadcast' | 'quota' | 'ends'

export interface Filters {
  q: string
  plan: string
  source: SourceFilter
  status: string
  access: AccessFilter
  sort: SortKey
  desc: boolean
  page: number
  size: 25 | 50 | 100 | 200
}

export const PAGE_SIZES = [25, 50, 100, 200] as const
export const DEFAULT_FILTERS: Filters = { q: '', plan: '', source: '', status: '', access: '', sort: 'email', desc: false, page: 0, size: 50 }

const SORTS: SortKey[] = ['email', 'created', 'last_broadcast', 'quota', 'ends']

export function filtersFromURL(p: URLSearchParams): Filters {
  const f = { ...DEFAULT_FILTERS }
  f.q = (p.get('q') ?? '').slice(0, 200)
  f.plan = /^[a-z0-9_-]{1,64}$/.test(p.get('plan') ?? '') ? p.get('plan')! : ''
  const src = p.get('source') ?? ''
  f.source = (['subscription', 'free', 'grant', 'legacy', 'ambassador'] as const).find((s) => s === src) ?? ''
  const st = p.get('status') ?? ''
  f.status = STATUSES.some((s) => s.value === st) ? st : ''
  const ac = p.get('access') ?? ''
  f.access = (['active', 'limited', 'blocked'] as const).find((s) => s === ac) ?? ''
  const so = p.get('sort') as SortKey
  if (SORTS.includes(so)) f.sort = so
  f.desc = p.get('order') === 'desc'
  const pg = Number(p.get('page'))
  f.page = Number.isInteger(pg) && pg > 0 && pg < 100000 ? pg - 1 : 0
  const sz = Number(p.get('size'))
  f.size = (PAGE_SIZES as readonly number[]).includes(sz) ? (sz as Filters['size']) : DEFAULT_FILTERS.size
  return f
}

/** The URL parameters of f (only what differs from the defaults). */
export function filtersToURL(f: Filters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.q) p.set('q', f.q)
  if (f.plan) p.set('plan', f.plan)
  if (f.source) p.set('source', f.source)
  if (f.status) p.set('status', f.status)
  if (f.access) p.set('access', f.access)
  if (f.sort !== DEFAULT_FILTERS.sort) p.set('sort', f.sort)
  if (f.desc) p.set('order', 'desc')
  if (f.page) p.set('page', String(f.page + 1))
  if (f.size !== DEFAULT_FILTERS.size) p.set('size', String(f.size))
  return p
}

/** The API query of f (export: no paging). */
export function apiQuery(f: Filters, page = true): URLSearchParams {
  const p = new URLSearchParams()
  if (page) {
    p.set('limit', String(f.size))
    if (f.page) p.set('offset', String(f.page * f.size))
  }
  if (f.q) p.set('q', f.q)
  if (f.plan) p.set('plan', f.plan)
  if (f.source) p.set('source', f.source)
  if (f.status) p.set('status', f.status)
  if (f.access === 'active') {
    p.set('account', 'active')
    p.set('access', 'full')
  } else if (f.access === 'limited') {
    p.set('account', 'active')
    p.set('access', 'no_new_streams')
  } else if (f.access === 'blocked') {
    p.set('account', 'blocked')
  }
  p.set('sort', f.sort)
  if (f.desc) p.set('order', 'desc')
  return p
}

/* ── presets (FilterPresets: at most four, All first; a preset sets the dropdowns it implies) ── */

export type Preset = 'all' | 'limited' | 'blocked' | 'gifts'

export const PRESETS: { id: Preset; label: string; set: Partial<Filters> }[] = [
  { id: 'all', label: 'All', set: {} },
  { id: 'limited', label: 'Limited', set: { access: 'limited' } },
  { id: 'blocked', label: 'Blocked', set: { access: 'blocked' } },
  { id: 'gifts', label: 'Gifts', set: { source: 'grant' } },
]

const PRESET_FIELDS = ['plan', 'source', 'status', 'access'] as const

/** The preset the dropdowns match exactly; undefined = changed by hand (no segment is on). */
export function activePreset(f: Filters): Preset | undefined {
  return PRESETS.find((p) => PRESET_FIELDS.every((k) => (p.set[k] ?? '') === f[k]))?.id
}

export function applyPreset(f: Filters, id: Preset): Filters {
  const p = PRESETS.find((x) => x.id === id)!
  return { ...f, plan: '', source: '', status: '', access: '', ...p.set, page: 0 }
}

export const hasHandFilters = (f: Filters) => PRESET_FIELDS.some((k) => f[k] !== '') && activePreset(f) === undefined

/* ── dropdown values ── */

export const SOURCES: { value: SourceFilter; label: string }[] = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'free', label: 'Free' },
  { value: 'grant', label: 'Gift' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'ambassador', label: 'Ambassador' },
]

export const STATUSES: { value: string; label: string }[] = [
  { value: 'trialing', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'trial_expired', label: 'Trial ended' },
  { value: 'none', label: 'No subscription' },
]

export const ACCESSES: { value: AccessFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'limited', label: 'Limited' },
  { value: 'blocked', label: 'Blocked' },
]

/** Plans the dropdown offers when the catalog is not readable. */
export const DEFAULT_PLANS = ['trial', 'free', 'creator', 'pro', 'business', 'legacy', 'ambassador']

/* ── words ── */

/** Hours as words (UsageMeters): "12 h 24 min", "20 h", "45 min", "0 min". */
export function hm(seconds: number): string {
  const m = Math.floor(Math.max(0, seconds) / 60)
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${r} min`
  return r ? `${h} h ${r} min` : `${h} h`
}

/** "12 h 24 min of 20 h · 62 %"; no limit: "2 h · no limit"; not on the plan: undefined. */
export function quotaText(q: AdminQuota | undefined): { text: string; warn: boolean } | undefined {
  if (!q) return undefined
  if (q.limit === null) return { text: `${hm(q.used)} · no limit`, warn: false }
  if (q.limit === 0) return undefined
  return { text: `${hm(q.used)} of ${hm(q.limit)} · ${Math.floor(q.percent ?? 0)} %`, warn: q.level >= 80 }
}

const pad = (n: number) => String(n).padStart(2, '0')
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Console time (11-console: UTC, 24 h): "2026-11-01 07:41:12 UTC". */
export function utc(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
}

/** A day and month, UTC (PL-08: "21 Nov"; another year: "12 Mar 2027"). */
export function day(iso?: string | null, now = Date.now()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getUTCFullYear() !== new Date(now).getUTCFullYear() ? ` ${d.getUTCFullYear()}` : ''
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${y}`
}

/** "31 Oct 18:42" (UTC). */
export function dayTime(iso?: string | null, now = Date.now()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${day(iso, now)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

/** Whole days until iso (≥ 0), for "· 2 d". */
export function daysLeft(iso: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((Date.parse(iso) - now) / 86_400_000))
}

const ENDS_WORD: Record<NonNullable<AdminUserBilling['ends_kind']>, string> = {
  grant: 'Gift',
  legacy: 'Legacy',
  ambassador: 'Review',
  trial: 'Trial',
  period: 'Period',
}

/** The "Ends" cell: "Trial · 3 Nov · 2 d"; warn within 3 days of a trial, gift or legacy end. */
export function endsText(b: AdminUserBilling | undefined, now = Date.now(), withDays = true): { text: string; warn: boolean; left?: number } | undefined {
  if (!b?.ends_at || !b.ends_kind) return undefined
  const left = daysLeft(b.ends_at, now)
  const soon = b.ends_kind !== 'period' && b.ends_kind !== 'ambassador' && left <= 7
  return { text: `${ENDS_WORD[b.ends_kind]} · ${day(b.ends_at, now)}${soon && withDays ? ` · ${left} d` : ''}`, warn: soon && left <= 3, ...(soon ? { left } : {}) }
}

/** Plan badge class (PL-02): trial and free — free, legacy — creator, ambassador — business. */
export function planTier(id: string): 'free' | 'creator' | 'pro' | 'business' {
  if (id === 'trial' || id === 'free') return 'free'
  if (id === 'legacy' || id === 'creator') return 'creator'
  if (id === 'pro') return 'pro'
  if (id === 'ambassador' || id === 'business') return 'business'
  return 'creator'
}

/** The access reason in words (FilterPresets: "Unpaid invoice · no new broadcasts"). */
export function accessReason(code?: string): string {
  switch (code) {
    case 'account_frozen':
      return 'Frozen · no new broadcasts'
    case 'account_streams_blocked':
      return 'Broadcasts blocked · no new broadcasts'
    case 'plan_payment_disputed':
      return 'Payment disputed · no new broadcasts'
    case 'plan_paused':
      return 'Plan paused · no new broadcasts'
    default:
      return 'No new broadcasts'
  }
}

/** A usage statement of GET /api/admin/users/{id}/usage (B3a PR5, docs/billing/metering.md). */
export type UsageStatement = {
  key: 'stream_hours_month' | 'captions_hours_month'
  period_start: string
  period_end: string
  period_kind: 'subscription' | 'trial' | 'calendar'
  plan_id: string
  quantity: number
  unit: 's'
  limit: number | null
  closed_at: string | null
  signature: string | null
  signature_valid?: boolean
  late_quantity?: number
}

export type AdminUserUsage = {
  usage: Record<string, { used: number; limit: number | null }>
  signed: boolean
  statements: UsageStatement[]
}
